import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  TablePagination,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { MoreVert as MoreVertIcon } from '@mui/icons-material'
import {
  SOCIAL_CONTENT_CHANGED_ERROR,
  socialApi,
  socialErrorCode,
  socialErrorStatus,
  type SocialCancelResponse,
  type SocialPaginationMeta,
  type SocialPublication,
} from '@/api/social'
import { SocialCancelDialog, SocialCancelResultAlert } from '@/components/admin/social/SocialCancelDialog'
import { SocialPostPreview } from '@/components/admin/social/SocialPostPreview'
import { targetLabel } from '@/components/admin/social/socialUi'
import { formatDateTimeDMY } from '@/utils/date'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { safeHttpUrl } from '@/utils/url'

const EMPTY_META: SocialPaginationMeta = { current_page: 1, last_page: 1, per_page: 10, total: 0 }

interface CardError {
  message: string
  /**
   * Подсказка после 409: reloaded — карточка перечитана, человек должен посмотреть
   * на новое содержимое; stale — содержимое разошлось с созданным при прежнем
   * content_hash, повторное одобрение снова даст 409, выход — отмена публикации.
   * У stale message — своя подсказка SPA, а не текст сервера: тот советует
   * обновить страницу, а это не помогает
   */
  hint: 'reloaded' | 'stale' | null
}

/** Вкладка «На одобрение»: публикации в awaiting_approval карточками с рендером постов */
export function SocialApprovalTab() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [publications, setPublications] = useState<SocialPublication[]>([])
  const [meta, setMeta] = useState<SocialPaginationMeta>(EMPTY_META)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [cardErrors, setCardErrors] = useState<Record<number, CardError>>({})
  const [cancelResult, setCancelResult] = useState<SocialCancelResponse | null>(null)

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [menuPublication, setMenuPublication] = useState<SocialPublication | null>(null)

  const [approveOpen, setApproveOpen] = useState(false)
  const [approveTarget, setApproveTarget] = useState<SocialPublication | null>(null)
  const [approving, setApproving] = useState(false)

  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelTarget, setCancelTarget] = useState<SocialPublication | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await socialApi.getPublications({
        status: 'awaiting_approval',
        page: page + 1,
        per_page: rowsPerPage,
      })
      // Последнюю карточку страницы одобрили или отменили — шаг назад
      if (result.data.length === 0 && page > 0) {
        setPage((current) => Math.max(0, current - 1))
        return
      }
      setPublications(result.data)
      setMeta(result.meta)
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('admin.social.loadFailed')))
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, t])

  useEffect(() => {
    void load()
  }, [load])

  const setCardError = (id: number, value: CardError | null) => {
    setCardErrors((prev) => {
      const next = { ...prev }
      if (value === null) {
        delete next[id]
      } else {
        next[id] = value
      }
      return next
    })
  }

  const clearNotices = () => {
    setError(null)
    setSuccess(null)
    setCancelResult(null)
  }

  /**
   * После 409 человек должен увидеть свежее содержимое, прежде чем одобрять снова.
   * social_content_changed при прежнем content_hash само не пройдёт: сервер пишет
   * хеш только при создании публикации, и повторное одобрение снова даст 409.
   */
  const reloadCard = async (id: number, sentHash: string, errorCode: string | null, message: string) => {
    try {
      const fresh = await socialApi.getPublication(id)
      if (fresh.status === 'awaiting_approval') {
        setPublications((prev) => prev.map((item) => (item.id === id ? fresh : item)))
        const stale = errorCode === SOCIAL_CONTENT_CHANGED_ERROR && fresh.content_hash === sentHash
        setCardError(
          id,
          stale
            ? { message: t('admin.social.approval.contentStaleHint'), hint: 'stale' }
            : { message, hint: 'reloaded' },
        )
        return
      }
    } catch {
      // Карточку не перечитать — перечитывается весь список ниже
    }

    setError(message)
    await load()
  }

  const handleApprove = async () => {
    const publication = approveTarget
    if (!publication) return

    setApproving(true)
    clearNotices()
    setCardError(publication.id, null)
    try {
      const response = await socialApi.approve(publication.id, publication.content_hash)
      setApproveOpen(false)
      setSuccess(response.message)
      await load()
    } catch (err) {
      setApproveOpen(false)
      const message = displayErrorMessage(getErrorMessage(err), t('admin.social.actionFailed'))
      if (socialErrorStatus(err) === 409) {
        await reloadCard(publication.id, publication.content_hash, socialErrorCode(err), message)
      } else {
        setCardError(publication.id, { message, hint: null })
      }
    } finally {
      setApproving(false)
    }
  }

  const openMenu = (event: React.MouseEvent<HTMLElement>, publication: SocialPublication) => {
    setMenuAnchor(event.currentTarget)
    setMenuPublication(publication)
  }

  const renderCard = (publication: SocialPublication) => {
    const newsUrl = safeHttpUrl(publication.news?.url)
    const newsTitle = publication.news?.title ?? t('admin.social.newsMissing', { id: publication.news_id })
    const cardError = cardErrors[publication.id]

    return (
      <Card key={publication.id} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="h6" gutterBottom sx={{ overflowWrap: 'anywhere' }}>
                {newsUrl ? (
                  <Link href={newsUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                    {newsTitle}
                  </Link>
                ) : (
                  newsTitle
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('admin.social.publishAt')}:{' '}
                {publication.publish_at ? formatDateTimeDMY(publication.publish_at) : t('admin.social.immediately')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                #{publication.id} · {t('admin.social.createdAt')} {formatDateTimeDMY(publication.created_at)}
              </Typography>
            </Box>
            <IconButton
              size="small"
              aria-label={t('admin.users.actions')}
              onClick={(event) => openMenu(event, publication)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>

          {cardError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setCardError(publication.id, null)}>
              {cardError.message}
              {cardError.hint === 'reloaded' && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {t('admin.social.approval.contentChangedHint')}
                </Typography>
              )}
            </Alert>
          )}

          {publication.news && !publication.news.is_public && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('admin.social.newsNotPublic')}
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
              mt: 2,
            }}
          >
            {publication.items.map((item) => (
              <Paper key={item.id} variant="outlined" sx={{ p: 2, minWidth: 0 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
                    {targetLabel(item.target, item.social_target_id)}
                  </Typography>
                  {item.target && (
                    <Chip size="small" variant="outlined" label={t(`admin.social.platforms.${item.target.platform}`)} />
                  )}
                  {item.target && !item.target.enabled && (
                    <Chip size="small" label={t('admin.social.targetDisabled')} />
                  )}
                </Box>
                {item.target ? (
                  <SocialPostPreview platform={item.target.platform} publication={publication} />
                ) : null}
              </Paper>
            ))}
          </Box>
        </CardContent>
        <CardActions sx={{ px: 2, pb: 2 }}>
          <Button
            variant="contained"
            size="small"
            sx={{ height: 40 }}
            disabled={cardError?.hint === 'stale'}
            onClick={() => {
              setApproveTarget(publication)
              setApproveOpen(true)
            }}
          >
            {t('admin.social.approval.publish')}
          </Button>
        </CardActions>
      </Card>
    )
  }

  return (
    <Box>
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {cancelResult && <SocialCancelResultAlert result={cancelResult} onClose={() => setCancelResult(null)} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        [1, 2].map((i) => (
          <Card key={i} sx={{ mb: 3 }}>
            <CardContent>
              <Skeleton variant="text" width="50%" height={32} />
              <Skeleton variant="text" width="30%" height={20} />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
                  gap: 2,
                  mt: 2,
                }}
              >
                <Skeleton variant="rounded" height={180} />
                <Skeleton variant="rounded" height={180} />
              </Box>
              <Skeleton variant="rounded" width={140} height={40} sx={{ mt: 2 }} />
            </CardContent>
          </Card>
        ))
      ) : meta.total === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">{t('admin.social.approval.empty')}</Typography>
        </Paper>
      ) : (
        publications.map(renderCard)
      )}

      {!loading && meta.total > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, overflow: 'hidden', minWidth: 0 }}>
          <TablePagination
            component="div"
            count={meta.total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 15, 25, 50]}
            labelRowsPerPage={isMobile ? '' : t('admin.social.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => t('admin.social.displayedRows', { from, to, count })}
          />
        </Box>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        TransitionProps={{ onExited: () => setMenuPublication(null) }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            if (menuPublication) {
              setCancelTarget(menuPublication)
              setCancelOpen(true)
            }
            setMenuAnchor(null)
          }}
        >
          {t('admin.social.approval.cancel')}
        </MenuItem>
      </Menu>

      <Dialog
        open={approveOpen}
        onClose={() => {
          if (!approving) setApproveOpen(false)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('admin.social.approval.confirmTitle')}</DialogTitle>
        <DialogContent>
          {approveTarget && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, overflowWrap: 'anywhere' }}>
                {approveTarget.news?.title ?? t('admin.social.newsMissing', { id: approveTarget.news_id })}
              </Typography>
              <Typography>{t('admin.social.approval.confirmTargets')}</Typography>
              <List dense>
                {approveTarget.items.map((item) => (
                  <ListItem key={item.id} disableGutters>
                    <ListItemText
                      primary={targetLabel(item.target, item.social_target_id)}
                      secondary={
                        item.target
                          ? [
                              t(`admin.social.platforms.${item.target.platform}`),
                              ...(item.target.enabled ? [] : [t('admin.social.targetDisabled')]),
                            ].join(' · ')
                          : undefined
                      }
                    />
                  </ListItem>
                ))}
              </List>
              {/* Шаги выключенных целей воркер не отправляет: они остаются pending до включения канала */}
              {approveTarget.items.some((item) => item.target && !item.target.enabled) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('admin.social.approval.confirmDisabledTargets')}
                </Alert>
              )}
              <Typography fontWeight={600}>
                {approveTarget.publish_at
                  ? t('admin.social.approval.confirmTime', { time: formatDateTimeDMY(approveTarget.publish_at) })
                  : t('admin.social.approval.confirmImmediately')}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)} disabled={approving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleApprove}
            disabled={approving || !approveTarget}
            startIcon={approving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('admin.social.approval.publish')}
          </Button>
        </DialogActions>
      </Dialog>

      <SocialCancelDialog
        open={cancelOpen}
        publication={cancelTarget}
        onClose={() => setCancelOpen(false)}
        onCancelled={(response) => {
          setCancelOpen(false)
          clearNotices()
          setCancelResult(response)
          void load()
        }}
      />
    </Box>
  )
}
